import { charters } from "@/mocks/aboutData";
import { useScrollReveal, useStaggerReveal } from "@/hooks/useScrollReveal";
import { usePublicRows } from "@/hooks/usePublicContent";
import Icon from "@/components/feature/Icon";
import type { DbDocument } from "@/types/database";

interface DocView {
  id: string | number;
  title: string;
  version?: string | null;
  date?: string | null;
  fileSize?: string | null;
  fileUrl?: string | null;
}

// Built-in fallback used until the `documents` table exists.
const FALLBACK: DbDocument[] = charters.map((c, i) => ({
  id: -(i + 1),
  title: c.title,
  category: "治理文件",
  version: c.version ?? null,
  date: c.date ?? null,
  file_size: c.fileSize ?? null,
  file_url: c.fileUrl ?? null,
  sort_order: i + 1,
  is_active: true,
  created_at: "",
  updated_at: "",
}));

function resolveFileHref(fileUrl: string): string {
  if (/^https?:\/\//.test(fileUrl)) return fileUrl;
  return encodeURI(`${import.meta.env.BASE_URL}${fileUrl.replace(/^\//, "")}`);
}

export default function CharterSection() {
  const { rows } = usePublicRows<DbDocument>("documents", FALLBACK);
  const docs: DocView[] = rows.map((d) => ({
    id: d.id,
    title: d.title,
    version: d.version,
    date: d.date,
    fileSize: d.file_size,
    fileUrl: d.file_url,
  }));

  const { ref: headerRef, style: headerStyle } = useScrollReveal();
  const { containerRef, getItemStyle } = useStaggerReveal(docs.length, 120);

  if (docs.length === 0) return null;

  return (
    <section className="py-24 md:py-32 bg-silver-50">
      <div className="px-6 md:px-10 lg:px-16">
        <div ref={headerRef} style={headerStyle} className="max-w-3xl mb-16 md:mb-20">
          <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-4">
            Governance
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950 leading-tight">
            協會章程與治理文件
          </h2>
        </div>

        <div ref={containerRef} className="grid md:grid-cols-3 gap-6 md:gap-8">
          {docs.map((doc, index) => (
            <div
              key={doc.id}
              style={getItemStyle(index)}
              className="group bg-white rounded-3xl p-8 border border-silver-200 hover:border-navy-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 rounded-2xl bg-navy-950/5 flex items-center justify-center group-hover:bg-navy-950 transition-colors duration-300">
                  <Icon className="ri-file-text-line text-2xl text-navy-950 group-hover:text-white transition-colors duration-300" />
                </div>
                {doc.version && (
                  <span className="text-xs font-medium text-silver-400 bg-silver-50 px-2 py-1 rounded">
                    {doc.version}
                  </span>
                )}
              </div>

              <h3 className="text-lg font-semibold text-navy-950 mb-2">
                {doc.title}
              </h3>
              <p className="text-sm text-silver-500 mb-6">
                {[doc.date, doc.fileSize].filter(Boolean).join(" · ") || "文件整理中"}
              </p>

              {doc.fileUrl ? (
                <a
                  href={resolveFileHref(doc.fileUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-navy-950 group-hover:text-navy-700 transition-colors"
                >
                  <span>下載文件</span>
                  <Icon className="ri-download-line group-hover:translate-y-0.5 transition-transform" />
                </a>
              ) : (
                <span className="inline-flex items-center gap-2 text-sm font-medium text-silver-400">
                  <Icon className="ri-time-line" />
                  <span>檔案整理中</span>
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
